/**
 * Client-side image downscaling/compression for media uploads.
 *
 * The video editor renders at 4K (3840px) at most, and AI models downscale
 * inputs internally anyway, so larger images only waste upload bandwidth, R2
 * storage, and downstream processing time. This re-encodes oversized raster
 * images before upload.
 *
 * Output format:
 * - 'webp'  — best compression, keeps transparency. Use where we control the
 *             consumer (e.g. the video editor / Remotion renderer).
 * - 'jpeg'  — universally accepted by every image/video AI provider. Use for
 *             model-facing uploads to avoid format-compatibility surprises.
 */

// Long edge cap — covers the 4K export resolution with headroom.
const MAX_IMAGE_DIMENSION = 3840;
const QUALITY = 0.85;
// Below this, compression isn't worth the quality loss.
const MIN_SIZE_TO_COMPRESS = 1024 * 1024; // 1MB

type OutputFormat = 'webp' | 'jpeg';

// Some browsers block reading pixels back from a canvas: Firefox with
// privacy.resistFingerprinting, and canvas-blocker extensions. The export then
// comes back blank (a solid black JPEG) while drawing raised no error, so we
// would upload a black image. Probe once: paint a known colour and read it
// back. Small differences are allowed for noise-based protections (Brave,
// Firefox's default fingerprinting protection), which keep images usable.
let canvasReadbackWorks: boolean | null = null;

function canReadCanvasPixels(): boolean {
  if (canvasReadbackWorks !== null) return canvasReadbackWorks;
  try {
    const probe = document.createElement('canvas');
    probe.width = 4;
    probe.height = 4;
    const ctx = probe.getContext('2d', { willReadFrequently: true });
    if (!ctx) return (canvasReadbackWorks = false);
    const expected = [20, 180, 90];
    ctx.fillStyle = `rgb(${expected.join(',')})`;
    ctx.fillRect(0, 0, 4, 4);
    const data = ctx.getImageData(0, 0, 4, 4).data;
    let ok = true;
    for (let i = 0; i < data.length; i += 4) {
      const off = expected.some((c, k) => Math.abs(data[i + k] - c) > 8);
      if (off || data[i + 3] < 250) ok = false;
    }
    canvasReadbackWorks = ok;
  } catch {
    canvasReadbackWorks = false;
  }
  return canvasReadbackWorks;
}

export async function compressImage(
  file: File,
  {
    // `force` skips the small-file short-circuit below. Pass it when the caller
    // needs the dimension cap guaranteed regardless of source file size (e.g. a
    // hard upload resolution limit), not just opportunistic shrinking.
    force = false,
    // Encoding of the result — see the file header. Defaults to WebP.
    format = 'webp',
  }: { force?: boolean; format?: OutputFormat } = {},
): Promise<File> {
  // Only compress JPEG and PNG — they're the real bloat source (large camera
  // photos and screenshots) and re-encode safely. JPEG is never animated;
  // animated PNG (APNG) exists but is rare enough not to special-case. WebP is
  // included only when the caller forces JPEG output, because downstream AI
  // models often reject WebP — round-tripping WebP to WebP would just lose
  // quality for no gain. Other formats (AVIF, GIF, SVG) are left untouched:
  // already compact, or animated/vector and unsafe to flatten through a canvas.
  const isWebpToJpeg = file.type === 'image/webp' && format === 'jpeg';
  if (file.type !== 'image/jpeg' && file.type !== 'image/png' && !isWebpToJpeg)
    return file;
  if (!force && file.size < MIN_SIZE_TO_COMPRESS) return file;
  // The canvas can't be read back here: upload the original rather than a
  // blank re-encode.
  if (!canReadCanvasPixels()) return file;

  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: 'from-image',
    });
    const { width, height } = bitmap;
    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }
    // JPEG has no alpha channel — flatten transparency onto white rather than
    // the canvas default (black), which would look broken.
    if (format === 'jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetW, targetH);
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close();

    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/webp';
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), mimeType, QUALITY),
    );

    if (!blob) return file;
    // Keep the original if re-encoding didn't actually save space — unless the
    // point was to change the container (e.g. WebP → JPEG for model uploads),
    // where the new file is what the caller actually needs.
    const formatChanged = mimeType !== file.type;
    if (!formatChanged && blob.size >= file.size) return file;

    const ext = format === 'jpeg' ? 'jpg' : 'webp';
    const newName = `${file.name.replace(/\.[^.]+$/, '')}.${ext}`;
    return new File([blob], newName, { type: mimeType });
  } catch {
    // Any failure (corrupt file, unsupported codec) — upload the original.
    return file;
  }
}
